import { Card } from "@components/shadcn-ui/card";
import { useIsMobile } from "@customHooks/use-mobile";
import { useDynamicHeight } from "@customHooks/useDynamicHeight";
import { toast } from "@customHooks/useToast";
import { applicationService } from "features/createApplication/service/applicationService";
import { ApplicationStepper } from "features/createApplication/components/ApplicationStepper";
import { useMultiStepForm } from "features/loanApplications/context/MultiStepFormContext";
import { loanFormFields } from "features/loanApplications/formFieldsConfig";
import { MultiStepLoanApplication } from "features/loanApplications/MultiStepForm";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { decodeURIComponent, encodeURIComponent } from "@lib/utils";
import { getProductTypeByValue } from "features/applications/config";
import PermissionWrapper from "features/Permission/PermissionWrapper";
import { Action, ApplicationState } from "features/Permission/types";
import Snipper from "@components/ui/Snipper";
import { ArrowLeft } from "lucide-react";

const FallBackUI = () => {
  return (
    <p className="flex w-full h-full gap-4 justify-center items-center">
      <Snipper />
      Checking permissions...
    </p>
  );
};

const DontHavePermission = () => {
  return (
    <p className="flex w-full h-full gap-4 justify-center items-center">
      You don't have permission to edit the applications.
    </p>
  );
};

const showToast = (variant: "default" | "destructive", description: string) => {
  toast({
    variant,
    description,
  });
};

function Header({ action }: { action: string | null }) {
  return (
    <strong className="block mt-0 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm">
      {action === "edit" ? "Edit Application" : "Create Application"}
    </strong>
  );
}

function StepContent({
  currentStep,
  isMobile,
  formFileds,
  goToStep,
  handleAction,
  fieldDisableState,
  applicationSatus,
  productType,
}: {
  applicationFormSteps: any;
  currentStep: any;
  isMobile: any;
  formFileds: any;
  goToStep: any;
  handleAction: any;
  fieldDisableState: any;
  applicationSatus: any;
  productType: any;
}) {
  return (
    <>
      <MultiStepLoanApplication
        goToStep={goToStep}
        currentStep={currentStep}
        formFileds={formFileds}
        handleAction={handleAction}
        fieldDisableState={fieldDisableState}
        isMobile={isMobile}
        applicationStatus={applicationSatus}
        productType={productType}
      />
    </>
  );
}

function ApplicationFormContainer({
  applicationFormSteps,
  currentStep,
  elementRef,
  goToStep,
  formFileds,
  handleAction,
  fieldDisableState,
  applicationSatus,
  productType,
}: any) {
  const isMobile = useIsMobile();

  return (
    <div
      className="flex flex-col py-0 scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-100"
      ref={elementRef}
      style={{ height: "calc(100% - 40px)" }}
    >
      {!isMobile && (
        <div className="flex justify-center sticky top-0 m-auto my-8">
          <ApplicationStepper
            stepsDetails={applicationFormSteps}
            onStepChange={goToStep}
            currentStep={currentStep}
          />
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-auto px-2">
        <StepContent
          applicationFormSteps={applicationFormSteps}
          currentStep={currentStep}
          isMobile={isMobile}
          formFileds={formFileds}
          goToStep={goToStep}
          fieldDisableState={fieldDisableState}
          handleAction={handleAction}
          applicationSatus={applicationSatus}
          productType={productType}
        />
      </div>
    </div>
  );
}

function CreateApplicationPage() {
  const [searchParams] = useSearchParams();
  const { currentStep, goToNextStep, goToStep, setLeadId } = useMultiStepForm();
  const { ref: elementRef, height } = useDynamicHeight();
  const { leadId } = useParams();
  const decodedLeadId = decodeURIComponent(leadId!);
  const action = searchParams.get("action");
  const isEditMode = action === "edit";
  const id = searchParams.get("id");
  const [applicationFormFields, setApplicationFormFields] =
    useState(loanFormFields);
  const [fetchedApplicationDetails, setFetchedApplicationDetails] = useState<{
    id?: string;
    leadId?: string;
    state?: ApplicationState;
    productType?: string;
  }>({});

  // Store leadId in context for use in address form
  useEffect(() => {
    if (decodedLeadId) {
      setLeadId(decodedLeadId);
      console.log("Setting lead ID in context:", decodedLeadId);
    }
  }, [decodedLeadId, setLeadId]);

  // If we're in edit mode and have an ID, also set it in the form
  useEffect(() => {
    if (isEditMode && fetchedApplicationDetails.leadId) {
      setLeadId(fetchedApplicationDetails.leadId);
      console.log("Setting lead ID from fetched details:", fetchedApplicationDetails.leadId);
    }
  }, [isEditMode, fetchedApplicationDetails.leadId, setLeadId]);

  useEffect(() => {
    const fetchFormDetails = async () => {
      try {
        const data = await applicationService.getApplicationById(id!);
        setFetchedApplicationDetails(data);
        const updatedFormData = loanFormFields.map((section: any) => ({
          ...section,
          fields: section.fields.map((field: any) => {
            let defaultValue =
              data[section.name]?.[field.name] || data[field.name];

            // Auto-fill PIL Member Name from Personal Details
            if (section.name === 'membershipDetails' && field.name === 'name') {
              const firstName = data.firstName || data.personalDetails?.firstName || "";
              const lastName = data.lastName || data.personalDetails?.lastName || "";
              const autoFilledName = `${firstName} ${lastName}`.trim();
              defaultValue = defaultValue || autoFilledName;
            }

            // Check if the field type is 'productType' and modify the default value accordingly
            if (field.name === "productType") {
              defaultValue =
                getProductTypeByValue(defaultValue) || defaultValue;
            }

            return {
              ...field,
              defaultValue,
            };
          }),
        }));

        setApplicationFormFields(updatedFormData);
        console.log("updatedFormData", updatedFormData);
      } catch (error) {
        console.error("Error fetching application details:", error);
      }
    };

    if (isEditMode && id) {
      fetchFormDetails();
    }
  }, [isEditMode, id]);

  // Generic function to perform the action and handle the response
  const performApplicationAction = async (
    formData: any,
    actionType: string,
    leadId?: string
  ) => {
    try {
      await applicationService.performAction(formData, actionType, leadId);

      // Success toast message
      let successMessage = "";
      if (actionType === "SAVE_APPLICATION") {
        successMessage = "Data Saved Successfully!";
      } else if (actionType === "CREATE_APPLICATION") {
        successMessage = `You have initiated an application successfully..!`;
      } else if (actionType === "SUBMIT_APPLICATION") {
        successMessage = "Application has been submitted Successfully!";
      }

      showToast("default", successMessage);
      goToNextStep();
    } catch (error) {
      const errorMessage =
        actionType === "CREATE_APPLICATION"
          ? "Something went wrong... could not initiate application"
          : "Something went wrong... Please try again";
      showToast("destructive", errorMessage);
      console.error(`Error in ${actionType.toLowerCase()}:`, error);
    }
  };

  const saveApplicationForm = async ({ formData }: any) => {
    performApplicationAction(formData, "SAVE_APPLICATION");
  };

  const handleApplicationInitialization = async ({ formData }: any) => {
    performApplicationAction(formData, "CREATE_APPLICATION", decodedLeadId);
  };

  const submitApplicationForm = async ({ formData }: any) => {
    performApplicationAction(formData, "SUBMIT_APPLICATION");
  };

  const handleAction = (actionType: string, formData: Object) => {
    console.log("Action type:", actionType, "Form data:", formData);
    if (actionType === "SAVE_APPLICATION") {
      saveApplicationForm({ formData });
    } else if (actionType === "CREATE_APPLICATION") {
      handleApplicationInitialization({ formData });
    } else if (actionType === "SUBMIT_APPLICATION") {
      submitApplicationForm({ formData });
    }
  };

  const fieldDisableState = {
    id: true,
    productType: true,
  };

  const applicationFormSteps = [...applicationFormFields];

  return (
    <>
      <Link
        to={`/dashboard/lead-details/${encodeURIComponent(
          fetchedApplicationDetails?.leadId ?? ""
        )}`}
        className="flex w-fit items-center gap-2 p-2 bg-slate-300 mb-1 rounded-md"
      >
        <ArrowLeft /> Back to Lead details page
      </Link>
      <Card className="relative h-full">
        <PermissionWrapper
          actions={[Action.EDIT_APPLICATION, Action.SUBMIT_APPLICATION]}
          fallback={<FallBackUI />}
        >
          {({ isAllowed }) =>
            isAllowed ? (
              <>
                <Header action={action} />
                <ApplicationFormContainer
                  applicationFormSteps={applicationFormSteps}
                  currentStep={currentStep}
                  id={id}
                  elementRef={elementRef}
                  height={height}
                  formFileds={applicationFormFields}
                  goToStep={goToStep}
                  fieldDisableState={fieldDisableState}
                  handleAction={handleAction}
                  applicationSatus={fetchedApplicationDetails.state}
                  productType={fetchedApplicationDetails.productType}
                />
              </>
            ) : (
              <DontHavePermission />
            )
          }
        </PermissionWrapper>
      </Card>
    </>
  );
}

export default CreateApplicationPage;
